import { AvatarSize } from '#app/components/Avatars/Avatar/types.js';
import { getAvatarSize } from '#app/components/Avatars/Avatar/utils.js';
import { Text } from '#app/components/Primitives/Text.js';
import { useAnalytics } from '#app/hooks/useAnalytics.js';
import { CONTEXT_TYPE_AVATAR } from './context/actionTypes.js';
import {
  type ProfileImageCropperContextData,
  type ProfileImageCropperContextTypes,
} from './types.js';
import { CameraIcon } from '@contra/icons/icons';
import { type MouseEvent, type RefObject } from 'react';
import { css, styled } from 'styled-components';

type HoverCropperButtonProps = Pick<
  ProfileImageCropperContextData,
  'handleUpload'
> & {
  readonly context?: ProfileImageCropperContextTypes;
  readonly formInputElement: RefObject<HTMLInputElement>;
  readonly shouldSaveProfileOnUpload: boolean;
  readonly size: AvatarSize;
};

const HoverButton = styled.div<{
  computedSize: number;
  variant: ProfileImageCropperContextTypes;
}>`
  ${({ computedSize, theme, variant }) => css`
    display: flex;
    justify-content: center;
    align-items: center;
    flex-direction: column;
    color: white;
    height: ${computedSize}px;
    width: ${computedSize}px;
    background: rgba(19, 19, 19, 0.6);
    position: absolute;
    top: 0;
    left: 0;
    opacity: 0;
    transition: opacity 400ms ease;
    z-index: 2;

    ${variant === 'AVATAR' &&
    css`
      border: 6px solid ${theme.colorsV2.white};
      border-radius: 50%;
    `}

    ${variant === 'LOGO' &&
    css`
      border-radius: 50%;
    `}

    &:hover {
      cursor: pointer;
      opacity: 1;
    }
  `}
`;

export const HoverCropperButton = ({
  context = CONTEXT_TYPE_AVATAR,
  formInputElement,
  handleUpload,
  shouldSaveProfileOnUpload,
  size,
}: HoverCropperButtonProps) => {
  const { track } = useAnalytics();

  return (
    <HoverButton
      aria-label="update profile picture"
      as="button"
      computedSize={getAvatarSize(size)}
      onClick={(event: MouseEvent<HTMLButtonElement>) => {
        track('button', {
          action: 'tapped',
          button_name: 'identity_inline_edit_avatar',
        });

        handleUpload(formInputElement, {
          context,
          shouldSaveProfileOnUpload,
        })(event);
      }}
      variant={context}
    >
      <CameraIcon size={32} />
      <Text fontWeight={600}>Change Photo</Text>
    </HoverButton>
  );
};
